import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Account } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { CaseType } from "@/api/rest/models/warranty";
import { ApiChatMessage } from "@/api/rest/services/chatService";
import {
  ChatMessageDto,
  deleteWarrantyDraft,
  processStructuredWarranty,
  StructuredWarrantyResponse,
  StructuredWarrantyRules,
  SubmitWarrantyArgs,
  WarrantyPhase,
  forceSubmitCase,
  getLatestWarrantyDraft,
  WarrantyDraftFlow,
} from "@/api/rest/services/warrantyService";

import { ChatMessage, ChatMessageHolder, ChatMessageType } from "@/components/common/ChatMessage";
import Button from "@/components/common/Button";
import { sendToSentry } from "@/utils/sentry";

import CategorySelectedBanner from "./CategorySelectedBanner";
import { createRequestGate } from "./requestGate";
import WarrantySubmissionForm from "./WarrantySubmissionForm";
import { useSubmissionForm } from "./useSubmissionForm";
import { ChatHistoryItem, hydrateStructuredWarrantyDraft } from "./warrantyDraftHydration";

interface StructuredWarrantyChatProps {
  householdData: HouseholdData;
  availableAccounts: Account[];
  refreshCases: () => void;
  rules: StructuredWarrantyRules;
}

const StructuredWarrantyChat: React.FC<StructuredWarrantyChatProps> = ({
  householdData,
  availableAccounts,
  refreshCases,
  rules,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [phase, setPhase] = useState<WarrantyPhase>(WarrantyPhase.TRIAGE);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [submissionData, setSubmissionData] = useState<SubmitWarrantyArgs | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [draftId, setDraftId] = useState<number | null>(null);
  // Input waits for the saved draft, so a first message is not overwritten by it. True even when the load fails.
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const gate = useMemo(createRequestGate, []);

  const {
    pendingFiles,
    uploadedDraftFiles,
    setUploadedDraftFiles,
    isSubmittingCase,
    setSubmittingState,
    isFileUploadMandatory,
    submitFiles,
    uploadPendingFiles,
    uploadProgress,
    submitError,
    setSubmitError,
    resetSubmission,
    clientSubmissionId,
  } = useSubmissionForm({ householdData, draftId });

  const buildingId = householdData.building?.buildingId ?? 0;

  const mapToApiHistory = useCallback((history: ChatHistoryItem[]): ChatMessageDto[] => {
    return history.map((item) => ({
      role: item.role,
      content: item.content,
    }));
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const token = gate.next();
    getLatestWarrantyDraft(householdData.household.id, WarrantyDraftFlow.STRUCTURED).then((result) => {
      if (isCancelled) {
        return;
      }
      setDraftLoaded(true);
      if (!gate.isCurrent(token) || !result.success || !result.result) {
        return;
      }

      const hydrated = hydrateStructuredWarrantyDraft(result.result);
      setDraftId(result.result.draftId);
      setUploadedDraftFiles(result.result.files);
      setChatHistory(hydrated.history);
      setPhase(hydrated.phase);
      setSelectedCategoryId(hydrated.selectedCategoryId);
      setSelectedCategoryName(hydrated.selectedCategoryName);
      setSubmissionData(hydrated.submissionData);
    });

    return () => {
      isCancelled = true;
    };
  }, [gate, householdData.household.id, setUploadedDraftFiles]);

  const handleResponse = useCallback((response: StructuredWarrantyResponse, currentHistory: ChatHistoryItem[]) => {
    // Add assistant message if present
    if (response.message) {
      setChatHistory([...currentHistory, { role: "assistant", content: response.message }]);
    }

    // Update phase and selection
    setPhase(response.phase);

    if (response.selectedCategoryId !== undefined) {
      setSelectedCategoryId(response.selectedCategoryId);
    }
    if (response.selectedCategoryName !== undefined) {
      setSelectedCategoryName(response.selectedCategoryName);
    }

    // Handle completion
    if (response.phase === WarrantyPhase.COMPLETED && response.submissionData) {
      setSubmissionData(response.submissionData);
    }
  }, []);

  const sendMessage = useCallback(async () => {
    const question = inputRef.current?.value.trim() ?? "";
    const selectedAccount = availableAccounts?.[0]?.id;
    if (!question || !selectedAccount || isLoading || !draftLoaded) {
      return;
    }

    inputRef.current!.value = "";
    setIsLoading(true);
    setRequestError(null);
    const token = gate.next();

    // Add user message to history
    const newHistory: ChatHistoryItem[] = [...chatHistory, { role: "user", content: question }];
    setChatHistory(newHistory);

    try {
      const result = await processStructuredWarranty({
        householdId: householdData.household.id,
        buildingId,
        history: mapToApiHistory(newHistory),
        phase,
        selectedCategoryId,
        accountId: selectedAccount,
        draftId,
        flowType: WarrantyDraftFlow.STRUCTURED,
      });

      if (!gate.isCurrent(token)) {
        return;
      }
      if (result.success) {
        const response = result.result;
        setDraftId(response.draftId ?? draftId);
        handleResponse(response, newHistory);
      } else {
        setRequestError(result.message);
        setChatHistory(newHistory.slice(0, -1));
        if (inputRef.current) {
          inputRef.current.value = question;
        }
      }
    } finally {
      if (gate.isCurrent(token)) {
        setIsLoading(false);
        // Delay focus until after React re-renders and enables the textarea
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  }, [
    availableAccounts,
    buildingId,
    chatHistory,
    handleResponse,
    householdData.household.id,
    isLoading,
    mapToApiHistory,
    phase,
    selectedCategoryId,
    draftId,
    draftLoaded,
    gate,
  ]);

  const handleCancelCategory = useCallback(() => {
    setPhase(WarrantyPhase.TRIAGE);
    setSelectedCategoryId(null);
    setSelectedCategoryName(null);
    setSubmissionData(null);
  }, []);

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
  };

  const handleFinalSubmit = useCallback(
    async (data: SubmitWarrantyArgs) => {
      const selectedAccount = availableAccounts?.[0]?.id;
      if (!selectedAccount) {
        return;
      }

      setSubmittingState(true);
      const uploads = await uploadPendingFiles();
      if (!uploads) {
        setSubmittingState(false);
        return;
      }

      const apiHistory: ApiChatMessage[] = chatHistory.map((item) => ({
        role: item.role,
        content: item.content,
      }));

      // Use category/subcontractor info from form submission (may have been changed by user)
      // Fall back to AI-selected values if not provided
      const finalSubcontractorName = data.subcontractorName ?? null;
      const finalCategoryId = data.categoryId !== undefined ? data.categoryId : selectedCategoryId;
      const finalCategoryName = data.categoryName !== undefined ? data.categoryName : selectedCategoryName;

      const result = await forceSubmitCase(
        {
          description:
            data.description + ("\n\n" + t("warranty.contactPersonsPhoneNumber") + ": " + phoneNumber.trim()),
          householdId: householdData.household.id,
          title: data.title,
          accountId: selectedAccount,
          phoneNumber: phoneNumber.trim(),
          type: CaseType.WARRANTY,
          subcontractorName: finalSubcontractorName,
          subcontractorEmail: data.subcontractorEmail,
          categoryId: finalCategoryId,
          categoryName: finalCategoryName,
          likelyUnderWarranty: data.likelyUnderWarranty,
          draftId,
          clientSubmissionId,
        },
        uploads,
        apiHistory,
      );

      if (result.success) {
        navigate(`/warranty/${result.result.id}`);
      } else {
        sendToSentry(new Error(result.message), {
          additionalContext: {
            component: "StructuredWarrantyChat.handleFinalSubmit",
            result,
          },
        });
        setSubmitError(result.message);
        setSubmittingState(false);
      }
    },
    [
      availableAccounts,
      chatHistory,
      householdData.household.id,
      navigate,
      uploadPendingFiles,
      setSubmitError,
      phoneNumber,
      draftId,
      selectedCategoryId,
      selectedCategoryName,
      setSubmittingState,
      t,
      clientSubmissionId,
    ],
  );

  // Clear first and invalidate pending replies, so a late reply cannot bring back the deleted draft.
  const resetChat = useCallback(async () => {
    gate.next();
    const discardedDraftId = draftId;
    setRequestError(null);
    setIsLoading(false);
    setDraftId(null);
    setChatHistory([]);
    setPhase(WarrantyPhase.TRIAGE);
    setSelectedCategoryId(null);
    setSelectedCategoryName(null);
    setSubmissionData(null);
    resetSubmission();
    setPhoneNumber("");
    if (discardedDraftId) {
      const result = await deleteWarrantyDraft(discardedDraftId);
      if (!result.success) {
        setRequestError(result.message);
      }
    }
    refreshCases();
  }, [gate, draftId, refreshCases, resetSubmission]);

  const showCategoryBanner = phase === WarrantyPhase.DETAIL_EXTRACTION && selectedCategoryName;

  return (
    <>
      <span className="flex items-center">
        <h3 className="text-xl font-semibold inline">{t("warranty.submitNewCase")}</h3>
        {chatHistory.length > 0 && (
          <Button variant="outline" size="sm" className="ml-2" onClick={resetChat}>
            {t("warranty.cancel")}
          </Button>
        )}
      </span>

      <div className="prose prose-sm prose-gray text-gray-500">
        <div>
          <ul className="m-0">
            <li className="m-0">{t("warranty.submitOneCase")}</li>
            {isFileUploadMandatory && <li className="m-0">{t("warranty.mandatoryFile")}</li>}
            {isFileUploadMandatory && <li className="m-0">{t("warranty.pictureDescription")}</li>}
            <li>
              <span className="inline-block">{t("warranty.problemDescriptionInstructions")}</span>
              <ul className="m-0">
                <li className="m-0">{t("warranty.defectDate")}</li>
                <li className="m-0">{t("warranty.defectLocation")}</li>
                <li className="m-0">{t("warranty.defectDescription")}</li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      {showCategoryBanner && (
        <CategorySelectedBanner categoryName={selectedCategoryName!} onCancel={handleCancelCategory} />
      )}

      {chatHistory.length > 0 && (
        <ChatMessageHolder>
          {chatHistory.map((m, i) => (
            <ChatMessage
              key={i}
              message={m.content}
              type={m.role === "assistant" ? ChatMessageType.RESPONSE : ChatMessageType.QUESTION}
            />
          ))}
          {isLoading && (
            <ChatMessage
              className="animate-pulse font-bold px-4 py-1 text-lg"
              message={"..."}
              type={ChatMessageType.RESPONSE}
            />
          )}
        </ChatMessageHolder>
      )}

      {phase !== WarrantyPhase.COMPLETED && (
        <>
          <textarea
            className="border border-gray-300 rounded-md p-2 w-full whitespace-pre-wrap"
            name="report"
            rows={5}
            placeholder={t("warranty.describeCase")}
            ref={inputRef}
            disabled={isLoading || !draftLoaded}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          {requestError && <p className="m-0 text-sm text-red-600">{requestError}</p>}
          {!isLoading && (
            <Button variant="primary" onClick={sendMessage} disabled={!draftLoaded}>
              {chatHistory.length > 0 ? t("warranty.answer") : t("warranty.startSubmission")}
            </Button>
          )}
        </>
      )}

      {phase === WarrantyPhase.COMPLETED && submissionData && (
        <>
          <hr className="border-2" />
          <WarrantySubmissionForm
            initialData={submissionData}
            subcontractors={rules.subcontractors}
            defaultCategory={rules.defaultCategory}
            selectedCategoryId={selectedCategoryId}
            onSubmit={handleFinalSubmit}
            isSubmitting={isSubmittingCase}
            pendingFiles={pendingFiles}
            uploadedDraftFiles={uploadedDraftFiles}
            submitFiles={submitFiles}
            uploadProgress={uploadProgress}
            submitError={submitError}
          >
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">{t("warranty.contactPersonsPhoneNumber")}</label>
              <input
                type="tel"
                className="border border-gray-300 rounded-md p-2 w-full"
                placeholder={t("warranty.phoneNumberPlaceholder")}
                value={phoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
              />
            </div>
          </WarrantySubmissionForm>
        </>
      )}
    </>
  );
};

export default StructuredWarrantyChat;
